import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { dealsApi } from '../api';
import type { DealReadWithInsightsAndTasks } from '../types/api';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper, 
  Grid,
  Container,
  Divider,
  Button
} from '@mui/material';
import { MeetingRoom as MeetingRoomIcon } from '@mui/icons-material';
import DealStageSelector from '../components/DealStageSelector';
import ActionableInsightsFeed from '../components/ActionableInsightsFeed';
import TaskList from '../components/TaskList';

const fetchDealDetails = async (id: string): Promise<DealReadWithInsightsAndTasks> => {
  return await dealsApi.getDeal(id);
};

export function DealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();

  const { data: deal, isLoading, isError } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => fetchDealDetails(dealId!),
    enabled: !!dealId,
  });

  const handleDealRoomClick = () => {
    navigate(`/deals/${dealId}/room`);
  };

  if (isLoading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError || !deal) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Error fetching deal details.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4">{deal?.name || 'Loading...'}</Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mt: 1 }}>
            ${deal?.amount?.toLocaleString() ?? '0'}
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<MeetingRoomIcon />}
          onClick={handleDealRoomClick}
          disabled={!deal}
        >
          Enter Deal Room
        </Button>
      </Box>
        
      <Divider sx={{ my: 2 }} />
        
      {/* Deal Stage Selector */}
      {deal && <DealStageSelector deal={deal} />}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={7}>
          {/* Tasks Section */}
          {deal?.id && <TaskList dealId={deal.id} />}
        </Grid>
        <Grid item xs={12} md={5}>
          {/* AI Insights Section */}
          <Paper sx={{ p: 2 }}>
            {deal && (
              <ActionableInsightsFeed 
                insights={deal.insights || []} 
                dealId={deal.id} 
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}